import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { incidentService } from '../../../services/incidentService';
import { useAuth } from '../../../contexts/AuthContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import NotesFeed from '../../../components/ui/NotesFeed';
import { companyService } from '../../../services/companyService';

const IncidentList = ({ incidents: initialIncidents, sites, selectedSiteId, onSiteChange, onDelete, onRestore }) => {
  const { userProfile } = useAuth();
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedType, setSelectedType] = React.useState('all');
  const [selectedSeverity, setSelectedSeverity] = React.useState('all');
  const [selectedStatus, setSelectedStatus] = React.useState('all');
  const [incidents, setIncidents] = React.useState(initialIncidents || []);
  const [updatingId, setUpdatingId] = React.useState(null);
  const [expandedNotesId, setExpandedNotesId] = React.useState(null);
  const [companyId, setCompanyId] = React.useState(null);

  React.useEffect(() => {
    setIncidents(initialIncidents || []);
  }, [initialIncidents]);

  React.useEffect(() => {
    companyService?.getUserCompany()?.then(result => {
      if (result?.data?.id) setCompanyId(result?.data?.id);
    });
  }, []);

  const canUpdateStatus = userProfile?.role === 'admin' || userProfile?.role === 'site_manager';

  const siteOptions = [
    { value: '', label: t('allSites') },
    ...sites?.map(site => ({
      value: site?.id,
      label: site?.site_name
    }))
  ];

  const typeOptions = [
    { value: 'all', label: t('allTypes') },
    { value: 'near_miss', label: t('nearMiss') },
    { value: 'injury', label: t('injury') },
    { value: 'property_damage', label: t('propertyDamage') },
    { value: 'unsafe_act', label: t('unsafeAct') },
    { value: 'unsafe_condition', label: t('unsafeCondition') },
    { value: 'other', label: t('other') }
  ];

  const severityOptions = [
    { value: 'all', label: t('allSeverities') },
    { value: 'low', label: t('low') },
    { value: 'medium', label: t('medium') },
    { value: 'high', label: t('high') },
    { value: 'critical', label: t('critical') }
  ];

  const statusOptions = [
    { value: 'all', label: t('allStatuses') },
    { value: 'open', label: t('open') },
    { value: 'investigating', label: t('investigating') },
    { value: 'resolved', label: t('resolved') }
  ];

  const statusUpdateOptions = [
    { value: 'open', label: t('open') },
    { value: 'investigating', label: t('investigating') },
    { value: 'resolved', label: t('resolved') }
  ];

  const getIncidentTypeLabel = (type) => {
    const labels = {
      near_miss: t('nearMiss'),
      injury: t('injury'),
      property_damage: t('propertyDamage'),
      unsafe_act: t('unsafeAct'),
      unsafe_condition: t('unsafeCondition'),
      other: t('other')
    };
    return labels?.[type] || type;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      low: 'text-success bg-success/10',
      medium: 'text-warning bg-warning/10',
      high: 'text-error bg-error/10',
      critical: 'text-error bg-error/20'
    };
    return colors?.[severity] || 'text-muted-foreground bg-muted';
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      open: 'bg-red-100 text-red-700 border border-red-200',
      investigating: 'bg-amber-100 text-amber-700 border border-amber-200',
      resolved: 'bg-green-100 text-green-700 border border-green-200'
    };
    return classes?.[status] || 'bg-muted text-muted-foreground';
  };

  const getStatusLabel = (status) => {
    const labels = { open: t('open'), investigating: t('investigating'), resolved: t('resolved') };
    return labels?.[status] || status;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleStatusChange = async (incidentId, newStatus) => {
    setUpdatingId(incidentId);
    const { data, error } = await incidentService?.updateIncidentStatus(incidentId, newStatus);
    if (!error && data) {
      setIncidents(prev => prev?.map(inc => inc?.id === incidentId ? { ...inc, status: newStatus } : inc));
    }
    setUpdatingId(null);
  };

  const filteredIncidents = incidents?.filter(incident => {
    if (searchQuery) {
      const searchLower = searchQuery?.toLowerCase();
      const matchesSearch =
        incident?.location_description?.toLowerCase()?.includes(searchLower) ||
        incident?.description?.toLowerCase()?.includes(searchLower) ||
        incident?.user_profiles?.full_name?.toLowerCase()?.includes(searchLower);
      if (!matchesSearch) return false;
    }
    if (selectedType !== 'all' && incident?.incident_type !== selectedType) return false;
    if (selectedSeverity !== 'all' && incident?.severity_level !== selectedSeverity) return false;
    if (selectedStatus !== 'all' && incident?.status !== selectedStatus) return false;
    return true;
  });

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedType('all');
    setSelectedSeverity('all');
    setSelectedStatus('all');
  };

  const hasActiveFilters = searchQuery || selectedType !== 'all' || selectedSeverity !== 'all' || selectedStatus !== 'all';

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">{t('incidentReports')}</h2>
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="text-sm text-accent hover:text-accent/80 font-medium flex items-center gap-1"
            >
              <Icon name="X" size={14} />
              {t('clearFilters')}
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="relative">
            <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t('searchIncidentsPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="pl-10"
            />
          </div>
          <Select
            options={siteOptions}
            value={selectedSiteId}
            onChange={onSiteChange}
            placeholder={t('filterBySite')}
          />
          <Select
            options={typeOptions}
            value={selectedType}
            onChange={setSelectedType}
          />
          <Select
            options={severityOptions}
            value={selectedSeverity}
            onChange={setSelectedSeverity}
          />
          <Select
            options={statusOptions}
            value={selectedStatus}
            onChange={setSelectedStatus}
          />
        </div>
      </div>
      {filteredIncidents?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-12 text-center">
          <Icon name="AlertTriangle" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {hasActiveFilters ? t('noMatchingIncidents') : t('noIncidentsReported')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {hasActiveFilters
              ? t('tryAdjustingFilters')
              : selectedSiteId
                ? t('noIncidentsForSite')
                : t('selectSiteToView')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredIncidents?.map((incident) => (
            <div key={incident?.id} className="bg-card border border-border rounded-lg shadow-elevation-2 p-4 md:p-6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                <div className="flex-1">
                  <div className="flex items-center flex-wrap gap-2 mb-2">
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent">
                      {getIncidentTypeLabel(incident?.incident_type)}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSeverityColor(incident?.severity_level)}`}>
                      {incident?.severity_level?.toUpperCase()}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(incident?.status || 'open')}`}>
                      {getStatusLabel(incident?.status || 'open')}
                    </span>
                    {incident?.is_read_only && (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                        {t('readOnly')}
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-1">
                    {incident?.location_description}
                  </h3>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Icon name="Calendar" size={14} />
                      <span>{formatDate(incident?.incident_date)}</span>
                    </div>
                    {incident?.user_profiles?.full_name && (
                      <div className="flex items-center gap-1">
                        <Icon name="User" size={14} />
                        <span>{t('reportedBy')} {incident?.user_profiles?.full_name}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {canUpdateStatus && (
                    <>
                      <span className="text-xs text-muted-foreground font-medium">{t('updateStatus')}:</span>
                      <select
                        value={incident?.status || 'open'}
                        onChange={(e) => handleStatusChange(incident?.id, e?.target?.value)}
                        disabled={updatingId === incident?.id}
                        className="text-xs border border-border rounded-md px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50 cursor-pointer"
                      >
                        {statusUpdateOptions?.map(opt => (
                          <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                        ))}
                      </select>
                      {updatingId === incident?.id && (
                        <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                      )}
                    </>
                  )}
                  {/* NSherjFh PR 5: admin Delete (active) or Restore (deleted),
                      mutually exclusive at the page level. */}
                  {onDelete && (
                    <button
                      onClick={() => onDelete(incident)}
                      className="flex items-center gap-1.5 text-xs font-medium text-destructive hover:text-destructive/80 px-2.5 py-1.5 rounded-md border border-destructive/30 hover:bg-destructive/5 transition-colors"
                      aria-label="Delete incident"
                    >
                      <Icon name="Trash2" size={13} />
                      Delete
                    </button>
                  )}
                  {onRestore && (
                    <button
                      onClick={() => onRestore(incident)}
                      className="flex items-center gap-1.5 text-xs font-medium text-accent hover:text-accent/80 px-2.5 py-1.5 rounded-md border border-accent/30 hover:bg-accent/5 transition-colors"
                      aria-label="Restore incident"
                    >
                      <Icon name="RotateCcw" size={13} />
                      Restore
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-1">{t('description')}</h4>
                  <p className="text-sm text-muted-foreground">{incident?.description}</p>
                </div>

                {incident?.injured_party && (
                  <div>
                    <h4 className="text-sm font-medium text-foreground mb-1">{t('injuredParty')}</h4>
                    <p className="text-sm text-muted-foreground">{incident?.injured_party}</p>
                  </div>
                )}

                {incident?.witness_name && (
                  <div>
                    <h4 className="text-sm font-medium text-foreground mb-1">{t('witness')}</h4>
                    <p className="text-sm text-muted-foreground">
                      {incident?.witness_name}
                      {incident?.witness_contact && ` (${incident?.witness_contact})`}
                    </p>
                  </div>
                )}

                {incident?.immediate_action_taken && (
                  <div>
                    <h4 className="text-sm font-medium text-foreground mb-1">{t('immediateActionTaken')}</h4>
                    <p className="text-sm text-muted-foreground">{incident?.immediate_action_taken}</p>
                  </div>
                )}

                {incident?.corrective_measures && (
                  <div>
                    <h4 className="text-sm font-medium text-foreground mb-1">{t('correctiveMeasures')}</h4>
                    <p className="text-sm text-muted-foreground">{incident?.corrective_measures}</p>
                  </div>
                )}

                {incident?.file_paths?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-foreground mb-2">{t('attachments')}</h4>
                    <div className="flex flex-wrap gap-2">
                      {incident?.file_paths?.map((path, index) => (
                        <div key={index} className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-md">
                          <Icon name="Paperclip" size={14} className="text-muted-foreground" />
                          <span className="text-xs text-foreground">{t('attachment')} {index + 1}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes toggle */}
                <div className="pt-2 border-t border-border">
                  <button
                    onClick={() => setExpandedNotesId(expandedNotesId === incident?.id ? null : incident?.id)}
                    className="flex items-center gap-2 text-sm font-medium text-accent hover:text-accent/80 transition-colors"
                  >
                    <Icon name="MessageSquare" size={15} />
                    {expandedNotesId === incident?.id ? 'Hide Notes' : 'View / Add Notes'}
                  </button>
                  {expandedNotesId === incident?.id && companyId && (
                    <div className="mt-3">
                      <NotesFeed
                        entityType="incident"
                        entityId={incident?.id}
                        companyId={companyId}
                        title="Incident Notes"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default IncidentList;